/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import * as React from "react";
import {ReactNode, useEffect} from "react";
import {Tab, Tabs} from "@mui/material";
import {styled} from "@mui/material/styles";
import {NumberParam, StringParam, useQueryParam, withDefault} from "use-query-params";

import ArgosFlexbox from "../Containers/ArgosFlexbox";

const CustomTabs = styled(Tabs)`
  & > .MuiTabs-scrollButtons.Mui-disabled {
    opacity: 0.3;
  }
`

type AlignItemDetailsHorizontal = 'left' | 'center' | 'right'

interface IHorizontalTabsProps {
  initContent: ReactNode
  initHtabIndex: number // TODO: This is not used if use htab name, but still needs to be defined. Change it to be optional.
  horizontalTabs: any[]
  align: AlignItemDetailsHorizontal
  favoriteType?: string
  uid: string
  outerDivProps?: any,
  padding?: any,
  contentDivProps?: any
  favIcon?: ReactNode
  tabNameUrlKey?: string
  initHtabName?: string
  contentBelowTabNames?: any
  actionButtons?: ReactNode
}

const NULL = 'null'

export const HorizontalTabs = (props: IHorizontalTabsProps) => {
  const htabParamId = `${props.uid}`
  const [htabIndex, setHtabIndex] = useQueryParam(htabParamId, NumberParam)
  // const initQueryParam = props?.tabNameUrlKey ? props?.tabNameUrlKey : props?.initHtabName
  const initQueryParam = props?.tabNameUrlKey
  const [htab, setHtab] = useQueryParam(initQueryParam ?? `${htabParamId}-name`, withDefault(StringParam, props?.initHtabName ?? NULL))
  const decodedHtab = decodeURI(htab ?? NULL)

  useEffect(() => {
    /**
     * If loading from link then we want to use htab name (any time htab is defined initially, then use it). Otherwise,
     * use initial htab index, and set htab name to the name of the tab at that index. If it's coming from link and
     * the htab name is not a param, then we want to use the htab index in the url not the initial tab index.
     */
    if (decodedHtab != NULL && decodedHtab != null) {
      const _htabIndex = getTabIndexFromName(decodedHtab ?? '', props.initHtabIndex)
      setHtabIndex(_htabIndex)
    } else {
      if (htabIndex == null) {
        setHtabIndex(props.initHtabIndex)
        if (props.horizontalTabs?.length > 0) setHtab(props.horizontalTabs[props.initHtabIndex]?.name ?? NULL)
        return
      }
      if (!(props.horizontalTabs?.length > 0)) {
        setHtabIndex(htabIndex)
        return
      }
      setHtab(props.horizontalTabs[htabIndex]?.name ?? NULL)
    }
  }, [props.horizontalTabs, props.initHtabIndex, htab]);

  const changeHorizontal = (event: React.SyntheticEvent, newValue: number) => {
    setHtabIndex(newValue)
    setHtab(props.horizontalTabs[newValue]?.name ?? NULL)
  };

  const getTabIndexFromName = (name: string, defaultIndex: number) => {
    return props.horizontalTabs?.findIndex((x: any) => x?.name == name) ?? defaultIndex
  }

  const shouldShowButtonsBelowTabs = props.contentBelowTabNames && decodedHtab in props.contentBelowTabNames
  const currentButtonsBelowTabs = props.contentBelowTabNames?.[decodedHtab]
  return (
    <div
      style={{
        display: 'flex',
        flexFlow: 'column nowrap',
        flexGrow: 1,
        overflow: 'auto',
        height: '100%',
        padding: 10,
        ...props.padding
      }}>
      <div style={{
        padding: 5,
        display: props.horizontalTabs?.length ? 'flex' : 'none',
        flexShrink: 0,
        justifyContent: "space-between",
        margin: 0,
        ...props.outerDivProps,

      }}>
        <CustomTabs
          orientation="horizontal"
          variant="scrollable"
          // scrollButtons={true}
          allowScrollButtonsMobile
          value={htabIndex}
          onChange={changeHorizontal}
        >
          {props.horizontalTabs?.map((v: any, i: number) => {
            return (
              <Tab label={v?.name} key={i} iconPosition={'end'}/>
            )
          })}
        </CustomTabs>
        <ArgosFlexbox noGap={true} style={{alignSelf: 'center', marginLeft: 'auto'}}>
          {props?.actionButtons}
          <div style={{alignSelf: 'center'}}>{props?.favIcon}</div>
        </ArgosFlexbox>

      </div>
      {shouldShowButtonsBelowTabs &&
        <div>
          {currentButtonsBelowTabs}
        </div>
      }
      <div style={{
        flexGrow: 1, overflow: 'auto', margin: 20, scrollbarGutter: 'stable',
        ...props?.contentDivProps
      }}>
        {props.horizontalTabs?.[htabIndex ?? props.initHtabIndex]?.content}
      </div>
    </div>
  )
}